import { getFileIcon } from '@/constants/node-styling';
import { useOrgStore } from '@/stores/org-store';
import { openArtifact } from '@/components/artifacts/open-artifact';
import { artifactTypeLabel } from '@/utils/artifact-type-label';
import { formatTimeAgo } from '@/utils/format-date';
import type { ArtifactListItem } from '@/types/artifacts';

interface RunArtifactCardProps {
  artifact: ArtifactListItem;
}

export function RunArtifactCard({ artifact }: RunArtifactCardProps) {
  const { currentOrg } = useOrgStore();
  const FileIcon = getFileIcon(artifact.title);

  const hasThumbnail =
    artifact.thumbnail_status === 'ready' && !!artifact.thumbnail_url;
  const typeLabel = artifactTypeLabel(artifact.type);

  const handleOpen = () => {
    if (currentOrg?.orgid) void openArtifact(currentOrg.orgid, artifact);
  };

  return (
    <div
      role='button'
      tabIndex={0}
      onClick={handleOpen}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleOpen();
        }
      }}
      className='group flex cursor-pointer flex-col overflow-clip rounded-xl border bg-card transition-colors hover:border-primary/50'
    >
      <div className='relative h-36 overflow-hidden bg-accent/30'>
        {hasThumbnail ? (
          <img
            src={artifact.thumbnail_url}
            alt={artifact.title}
            className='pointer-events-none absolute inset-0 size-full object-cover object-top'
          />
        ) : (
          <div className='flex size-full items-center justify-center'>
            <FileIcon className='size-10 opacity-30' />
          </div>
        )}
      </div>
      <div className='flex flex-col gap-1 p-3'>
        <p className='truncate text-sm font-medium text-foreground'>
          {artifact.title}
        </p>
        <div className='flex min-w-0 items-center gap-1.5 text-xs text-muted-foreground'>
          <span className='shrink-0'>{typeLabel}</span>
          <span className='size-1 shrink-0 rounded-full bg-muted-foreground/50' />
          <span className='truncate'>
            Last updated {formatTimeAgo(artifact.updated_at)}
          </span>
        </div>
      </div>
    </div>
  );
}
